import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Fuel, Car, CreditCard, Upload, MapPin, ReceiptText, Wallet, Banknote, Camera, Sparkles, Loader2, RefreshCw, PenLine } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CARD_IMAGES } from "../lib/cards";
import { ReceiptOverlay, AiBadge } from "../components/ReceiptScan";

function todayStr() { return new Date().toISOString().slice(0, 10); }
function nowTime() { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; }

function SectionTitle({ icon: Icon, children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" /> {children}
    </p>
  );
}

export default function RefuelForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPemandu = user?.role === "pemandu";
  const [vehicles, setVehicles] = useState([]);
  const [assignedVehicles, setAssignedVehicles] = useState([]);
  const [fuelPrices, setFuelPrices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiFields, setAiFields] = useState(null);
  const [aiFilled, setAiFilled] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [aiLinkOff, setAiLinkOff] = useState(false);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);
  const [form, setForm] = useState({
    vehicle_id: "", date: todayStr(), time: nowTime(), odometer: "",
    station_location: "", station_lat: null, station_lng: null,
    fuel_type: "", used_budi: false, amount_rm: "", liters: "",
    payment_method: "", receipt_image: "", receipt_filename: "",
  });
  const [payCategory, setPayCategory] = useState("");
  const [locSuggestions, setLocSuggestions] = useState([]);
  const locTimer = useRef(null);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/fuel-prices").then((r) => setFuelPrices(r.data.items));
    api.get("/bookings").then((r) => {
      const active = r.data.filter((b) => ["diluluskan", "menunggu_kelulusan"].includes(b.status) && b.vehicle_id);
      const seen = new Set();
      const list = [];
      active.forEach((b) => { if (!seen.has(b.vehicle_id)) { seen.add(b.vehicle_id); list.push({ id: b.vehicle_id, reg: b.vehicle_reg, dest: b.destination }); } });
      setAssignedVehicles(list);
      if (list.length > 0) setForm((f) => ({ ...f, vehicle_id: f.vehicle_id || list[0].id }));
    });
  }, []);

  const selVehicle = vehicles.find((v) => v.id === form.vehicle_id);
  useEffect(() => {
    if (selVehicle && !form.odometer) setForm((f) => ({ ...f, odometer: selVehicle.current_odometer }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selVehicle?.id]);

  const fp = fuelPrices.find((p) => p.fuel_type === form.fuel_type);
  const activePrice = fp ? (form.used_budi && fp.budi_price ? fp.budi_price : fp.price_per_liter) : null;

  const pickFuelType = (ft) => {
    const p = fuelPrices.find((x) => x.fuel_type === ft);
    const price = p ? (form.used_budi && p.budi_price ? p.budi_price : p.price_per_liter) : null;
    setForm((f) => ({
      ...f, fuel_type: ft,
      used_budi: p?.budi_price ? f.used_budi : false,
      liters: price && f.amount_rm ? (Number(f.amount_rm) / price).toFixed(2) : f.liters,
    }));
  };

  const toggleBudi = (on) => {
    const price = fp ? (on && fp.budi_price ? fp.budi_price : fp.price_per_liter) : null;
    setForm((f) => ({ ...f, used_budi: on, liters: price && f.amount_rm ? (Number(f.amount_rm) / price).toFixed(2) : f.liters }));
  };

  const onAmountChange = (val) => {
    setForm((f) => ({ ...f, amount_rm: val, liters: !aiLinkOff && activePrice && val ? (Number(val) / activePrice).toFixed(2) : f.liters }));
  };
  const onLitersChange = (val) => {
    setForm((f) => ({ ...f, liters: val, amount_rm: !aiLinkOff && activePrice && val ? (Number(val) * activePrice).toFixed(2) : f.amount_rm }));
  };

  const onLocationType = (val) => {
    setForm((f) => ({ ...f, station_location: val, station_lat: null, station_lng: null }));
    clearTimeout(locTimer.current);
    if (val.trim().length < 3) { setLocSuggestions([]); return; }
    locTimer.current = setTimeout(() => {
      api.get("/geo/search", { params: { q: val } }).then((r) => setLocSuggestions(r.data)).catch(() => {});
    }, 450);
  };
  const pickLocation = (s) => {
    setForm((f) => ({ ...f, station_location: s.name, station_lat: s.lat, station_lng: s.lng }));
    setLocSuggestions([]);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Sila muat naik fail imej (foto resit)"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Saiz imej melebihi 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, receipt_image: reader.result, receipt_filename: file.name }));
      analyzeReceipt(reader.result);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const analyzeReceipt = async (image) => {
    setAnalyzing(true);
    setAiFields(null);
    setAiFilled([]);
    setActiveField(null);
    try {
      const { data } = await api.post("/refuels/analyze-receipt", { image_base64: image });
      const fx = data.fields || {};
      setAiFields(fx);
      const val = (k) => (fx[k] && fx[k].value !== null && fx[k].value !== undefined ? fx[k].value : null);
      const filled = [];
      const patch = {};
      if (val("date")) { patch.date = val("date"); filled.push("date"); }
      if (val("time")) { patch.time = String(val("time")).slice(0, 5); filled.push("time"); }
      if (val("station_name")) { patch.station_location = val("station_name"); filled.push("station_name"); }
      if (val("fuel_type")) { patch.fuel_type = val("fuel_type"); filled.push("fuel_type"); }
      if (val("liters")) { patch.liters = String(val("liters")); filled.push("liters"); }
      if (val("amount_rm")) { patch.amount_rm = String(val("amount_rm")); filled.push("amount_rm"); }
      if (val("used_budi") === true) { patch.used_budi = true; filled.push("used_budi"); }
      if (val("payment") === "tunai" || val("payment") === "kad_kredit_debit") { patch.payment_method = val("payment"); filled.push("payment"); }
      setForm((f) => ({ ...f, ...patch }));
      if (filled.includes("liters") && filled.includes("amount_rm")) setAiLinkOff(true);
      if (val("station_name")) {
        api.get("/geo/search", { params: { q: val("station_name") } })
          .then((r) => {
            if (r.data?.[0]) setForm((f) => ({ ...f, station_lat: r.data[0].lat, station_lng: r.data[0].lng }));
          })
          .catch(() => {});
      }
      const pay = val("payment");
      if (pay === "tunai" || pay === "kad_kredit_debit") setPayCategory("wang_sendiri");
      setAiFilled(filled);
      toast.success(`AI berjaya mengisi ${filled.length} medan dari resit — sila semak sebelum hantar`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAnalyzing(false);
    }
  };

  const clearReceipt = () => {
    setForm((f) => ({ ...f, receipt_image: "", receipt_filename: "" }));
    setAiFields(null);
    setAiFilled([]);
    setActiveField(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error("Sila pilih kenderaan"); return; }
    if (!form.fuel_type) { toast.error("Sila pilih jenis minyak"); return; }
    if (!form.payment_method) { toast.error("Sila pilih kaedah bayaran"); return; }
    if (!form.receipt_image) { toast.error("Resit wajib dimuat naik"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/refuels", {
        vehicle_id: form.vehicle_id,
        station_location: form.station_location,
        station_lat: form.station_lat,
        station_lng: form.station_lng,
        fuel_type: form.fuel_type,
        used_budi: form.used_budi,
        odometer: Number(form.odometer),
        refuel_datetime: `${form.date}T${form.time}`,
        liters: Number(form.liters),
        amount_rm: Number(form.amount_rm),
        payment_method: form.payment_method,
        receipt_image: form.receipt_image,
        receipt_filename: form.receipt_filename || null,
        ai_fields: aiFields,
      });
      toast.success(data.matched_booking_id
        ? `Isi minyak direkodkan & dipadankan dengan tempahan ke ${data.matched_destination}`
        : "Isi minyak direkodkan (tiada tempahan sepadan pada waktu tersebut)");
      navigate("/isi-minyak");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`space-y-4 fade-up ${form.receipt_image ? "max-w-6xl" : "max-w-lg"} mx-auto pb-8`} data-testid="refuel-form-page">
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="refuel-camera-input" />
      <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="refuel-upload-input" />
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => navigate("/isi-minyak")} data-testid="refuel-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Rekod Isi Minyak</h1>
          <p className="text-sm text-muted-foreground">Ambil gambar resit — AI akan mengisi borang secara automatik untuk anda.</p>
        </div>
      </div>

      {!form.receipt_image && !manualMode && (
        <div className="space-y-3" data-testid="refuel-capture-choice">
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => cameraRef.current?.click()} data-testid="refuel-choice-camera"
              className="border-2 border-[#10056E]/30 bg-white rounded-2xl p-6 text-center hover:border-[#10056E] hover:shadow-md transition-all flex flex-col items-center justify-start min-h-40">
              <span className="h-14 w-14 rounded-full bg-[#10056E] text-white flex items-center justify-center">
                <Camera className="h-7 w-7" />
              </span>
              <p className="text-sm font-bold mt-3">Ambil Gambar</p>
              <p className="text-[11px] text-muted-foreground mt-1">Buka kamera telefon anda</p>
            </button>
            <button type="button" onClick={() => uploadRef.current?.click()} data-testid="refuel-choice-upload"
              className="border-2 border-slate-200 bg-white rounded-2xl p-6 text-center hover:border-[#10056E] hover:shadow-md transition-all flex flex-col items-center justify-start min-h-40">
              <span className="h-14 w-14 rounded-full bg-slate-100 text-[#10056E] flex items-center justify-center">
                <Upload className="h-7 w-7" />
              </span>
              <p className="text-sm font-bold mt-3">Muat Naik</p>
              <p className="text-[11px] text-muted-foreground mt-1">Pilih dari galeri / fail</p>
            </button>
          </div>
          <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-3 flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-[#10056E] mt-0.5 shrink-0" />
            <p className="text-xs text-slate-700">AI akan membaca resit anda (Shell, PETRONAS, BUDI dll) dan mengisi tarikh, masa, stesen, jenis minyak, liter & jumlah secara automatik. Anda hanya perlu semak & hantar.</p>
          </div>
          <button type="button" onClick={() => setManualMode(true)} data-testid="refuel-manual-link"
            className="w-full text-center text-xs text-muted-foreground hover:text-[#10056E] underline underline-offset-2 py-1">
            <PenLine className="h-3 w-3 inline mr-1" />Isi borang secara manual
          </button>
        </div>
      )}

      {(form.receipt_image || manualMode) && (
      <div className={form.receipt_image ? "grid lg:grid-cols-[minmax(300px,42%)_1fr] gap-5 items-start" : ""}>
        {form.receipt_image && (
          <div className="lg:sticky lg:top-4 space-y-3" data-testid="refuel-receipt-panel">
            {analyzing ? (
              <div className="border rounded-xl p-4 bg-white space-y-3">
                <img src={form.receipt_image} alt="Resit" className="w-full rounded-lg border opacity-60" />
                <p className="text-sm font-semibold text-[#10056E] flex items-center gap-2 justify-center" data-testid="refuel-analyzing">
                  <Loader2 className="h-4 w-4 animate-spin" /> AI sedang menganalisis resit...
                </p>
              </div>
            ) : (
              <ReceiptOverlay image={form.receipt_image} fields={aiFields} activeField={activeField} onPickField={(k) => setActiveField(activeField === k ? null : k)} />
            )}
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => cameraRef.current?.click()} data-testid="refuel-retake-btn">
                <Camera className="h-3.5 w-3.5 mr-1.5" /> Ambil Semula
              </Button>
              <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => uploadRef.current?.click()} data-testid="refuel-reupload-btn">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Tukar Gambar
              </Button>
              {aiFields && (
                <Button type="button" size="sm" variant="outline" className="flex-1 border-[#10056E] text-[#10056E]" onClick={() => analyzeReceipt(form.receipt_image)} data-testid="refuel-reanalyze-btn">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Analisis Semula
                </Button>
              )}
            </div>
            {aiFields && (
              <p className="text-[11px] text-muted-foreground">Klik medan borang atau kotak berwarna untuk melihat dari mana maklumat diambil. <b>{aiFilled.length} medan</b> diisi oleh AI.</p>
            )}
          </div>
        )}

      <form onSubmit={submit} className="space-y-6">
        {/* Kenderaan & Masa */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-4">
          <SectionTitle icon={Car}>Kenderaan & Masa</SectionTitle>
          {isPemandu ? (
            assignedVehicles.length > 0 ? (
              <div className="space-y-1.5">
                <Label>Kenderaan Tugasan Semasa Anda</Label>
                <div className="space-y-2" data-testid="refuel-assigned-vehicles">
                  {assignedVehicles.map((v) => (
                    <button key={v.id} type="button" onClick={() => setForm((f) => ({ ...f, vehicle_id: v.id, odometer: "" }))}
                      data-testid={`refuel-assigned-${v.reg}`}
                      className={`w-full text-left border rounded-lg p-3 transition-colors flex items-center gap-3 ${form.vehicle_id === v.id ? "border-[#10056E] ring-1 ring-[#10056E] bg-indigo-50/50" : "border-slate-200"}`}>
                      <Car className="h-5 w-5 text-[#10056E] shrink-0" />
                      <span>
                        <span className="block text-sm font-bold">{v.reg}</span>
                        <span className="block text-xs text-muted-foreground">Tempahan: {v.dest}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="refuel-no-assignment">
                  Tiada kenderaan ditugaskan kepada anda buat masa ini. Sila pilih kenderaan secara manual.
                </p>
                <Select value={form.vehicle_id} onValueChange={(v) => setForm((f) => ({ ...f, vehicle_id: v, odometer: "" }))}>
                  <SelectTrigger className="h-11" data-testid="refuel-vehicle-select"><SelectValue placeholder="Pilih kenderaan" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_number} — {v.make} {v.model}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )
          ) : (
            <div className="space-y-1.5">
              <Label>Kenderaan <span className="text-[#DC3545]">*</span></Label>
              <Select value={form.vehicle_id} onValueChange={(v) => setForm((f) => ({ ...f, vehicle_id: v, odometer: "" }))}>
                <SelectTrigger className="h-11" data-testid="refuel-vehicle-select"><SelectValue placeholder="Pilih kenderaan" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_number} — {v.make} {v.model}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tarikh <span className="text-[#DC3545]">*</span>{aiFilled.includes("date") && <AiBadge />}</Label>
              <Input type="date" required className="h-11" value={form.date} onFocus={() => setActiveField("date")} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="refuel-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Masa <span className="text-[#DC3545]">*</span>{aiFilled.includes("time") && <AiBadge />}</Label>
              <Input type="time" required className="h-11" value={form.time} onFocus={() => setActiveField("time")} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="refuel-time-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Bacaan Odometer Semasa (KM) <span className="text-[#DC3545]">*</span></Label>
            <Input type="number" min="0" step="0.1" required className="h-11" value={form.odometer}
              onChange={(e) => setForm({ ...form, odometer: e.target.value })} data-testid="refuel-odometer-input" />
            {selVehicle && <p className="text-xs text-muted-foreground">Bacaan terakhir sistem: {Number(selVehicle.current_odometer).toLocaleString()} km</p>}
          </div>
        </div>

        {/* Lokasi Stesen */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
          <SectionTitle icon={MapPin}>Lokasi Stesen {aiFilled.includes("station_name") && <AiBadge />}</SectionTitle>
          <div className="relative">
            <MapPin className="h-4 w-4 absolute left-3 top-[22px] -translate-y-1/2 text-slate-400" />
            <Input required className="pl-9 h-11" placeholder="Taip untuk cari lokasi stesen minyak..." value={form.station_location}
              onFocus={() => setActiveField("station_name")}
              onChange={(e) => onLocationType(e.target.value)} data-testid="refuel-location-input" autoComplete="off" />
            {locSuggestions.length > 0 && (
              <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto" data-testid="refuel-location-suggestions">
                {locSuggestions.map((s, i) => (
                  <button key={i} type="button" onClick={() => pickLocation(s)} data-testid={`refuel-location-option-${i}`}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-indigo-50 border-b last:border-0">
                    <MapPin className="h-3.5 w-3.5 inline mr-1.5 text-[#10056E]" />{s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {form.station_lat && (
            <div className="rounded-lg overflow-hidden border" data-testid="refuel-location-map">
              <MapContainer key={`${form.station_lat}-${form.station_lng}`} center={[form.station_lat, form.station_lng]} zoom={15}
                scrollWheelZoom={false} dragging={false} style={{ height: 150, width: "100%", zIndex: 0 }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                <CircleMarker center={[form.station_lat, form.station_lng]} radius={10} pathOptions={{ color: "#10056E", fillColor: "#10056E", fillOpacity: 0.7 }} />
              </MapContainer>
            </div>
          )}
        </div>

        {/* Jenis Minyak & Jumlah */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-4">
          <SectionTitle icon={Fuel}>Jenis Minyak & Jumlah</SectionTitle>
          <div className="space-y-1.5">
            <Label>Jenis Minyak <span className="text-[#DC3545]">*</span>{aiFilled.includes("fuel_type") && <AiBadge />}</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="refuel-fuel-types" onClick={() => setActiveField("fuel_type")}>
              {fuelPrices.map((p) => (
                <button key={p.fuel_type} type="button" onClick={() => pickFuelType(p.fuel_type)} data-testid={`refuel-fuel-${p.fuel_type.replace(/[^a-zA-Z0-9]/g, "")}`}
                  className={`border rounded-lg p-3 text-sm font-semibold text-left transition-colors ${form.fuel_type === p.fuel_type ? "border-[#10056E] ring-1 ring-[#10056E] bg-indigo-50/50" : "border-slate-200"}`}>
                  {p.fuel_type}
                  <span className="block text-[11px] font-normal text-muted-foreground mt-0.5">RM {Number(p.price_per_liter).toFixed(2)}/L</span>
                </button>
              ))}
            </div>
          </div>
          {fp && (
            <div className="bg-slate-50 border rounded-lg p-3 space-y-2" data-testid="refuel-price-info">
              <p className="text-sm">Harga semasa: <b>RM {Number(activePrice).toFixed(2)}/L</b>{form.used_budi ? " (Harga BUDI)" : ""}</p>
              {fp.budi_price != null && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Guna Harga BUDI (RM {Number(fp.budi_price).toFixed(2)}/L){aiFilled.includes("used_budi") && <AiBadge />}</p>
                  <Switch checked={form.used_budi} onCheckedChange={toggleBudi} data-testid="refuel-budi-toggle" />
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Jumlah (RM) <span className="text-[#DC3545]">*</span>{aiFilled.includes("amount_rm") && <AiBadge />}</Label>
              <Input type="number" min="0.01" step="any" required className="h-11" value={form.amount_rm}
                onFocus={() => setActiveField("amount_rm")}
                onChange={(e) => onAmountChange(e.target.value)} data-testid="refuel-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Liter <span className="text-[#DC3545]">*</span>{aiFilled.includes("liters") && <AiBadge />}</Label>
              <Input type="number" min="0.001" step="any" required className="h-11" value={form.liters}
                onFocus={() => setActiveField("liters")}
                onChange={(e) => onLitersChange(e.target.value)} data-testid="refuel-liters-input" />
            </div>
          </div>
          {activePrice && <p className="text-xs text-muted-foreground">Pengiraan automatik berdasarkan RM {Number(activePrice).toFixed(2)}/L — anda bebas melaraskan kedua-dua nilai.</p>}
        </div>

        {/* Kaedah Bayaran */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-4">
          <SectionTitle icon={CreditCard}>Kaedah Bayaran</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => { setPayCategory("kad_minyak"); setForm((f) => ({ ...f, payment_method: "" })); }}
              data-testid="refuel-paycat-kad"
              className={`border rounded-lg p-3.5 text-left transition-colors ${payCategory === "kad_minyak" ? "border-[#10056E] ring-1 ring-[#10056E] bg-indigo-50/50" : "border-slate-200"}`}>
              <CreditCard className="h-5 w-5 text-[#10056E] mb-1.5" />
              <p className="text-sm font-bold">Kad Minyak</p>
              <p className="text-[11px] text-muted-foreground">Kad fleet agensi</p>
            </button>
            <button type="button" onClick={() => { setPayCategory("wang_sendiri"); setForm((f) => ({ ...f, payment_method: "" })); }}
              data-testid="refuel-paycat-sendiri"
              className={`border rounded-lg p-3.5 text-left transition-colors ${payCategory === "wang_sendiri" ? "border-[#10056E] ring-1 ring-[#10056E] bg-indigo-50/50" : "border-slate-200"}`}>
              <Wallet className="h-5 w-5 text-[#10056E] mb-1.5" />
              <p className="text-sm font-bold">Bayaran Sendiri</p>
              <p className="text-[11px] text-muted-foreground">Tuntutan semula</p>
            </button>
          </div>
          {payCategory === "kad_minyak" && (
            <div className="grid grid-cols-2 gap-3" data-testid="refuel-kad-options">
              {[["petronas_smartpay", "PETRONAS SmartPay"], ["shell_card", "Shell Card"]].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setForm((f) => ({ ...f, payment_method: val }))}
                  data-testid={`refuel-payment-${val}`}
                  className={`border rounded-xl p-2.5 transition-colors ${form.payment_method === val ? "border-[#10056E] ring-2 ring-[#10056E]" : "border-slate-200"}`}>
                  <img src={CARD_IMAGES[val]} alt={label} className="w-full rounded-lg object-cover aspect-[8/5]" />
                  <p className="text-xs font-bold mt-2 text-center">{label}</p>
                </button>
              ))}
            </div>
          )}
          {payCategory === "wang_sendiri" && (
            <div className="grid grid-cols-2 gap-3" data-testid="refuel-sendiri-options">
              {[["tunai", "Tunai", Banknote], ["kad_kredit_debit", "Kad Kredit/Debit", CreditCard]].map(([val, label, Icon]) => (
                <button key={val} type="button" onClick={() => setForm((f) => ({ ...f, payment_method: val }))}
                  data-testid={`refuel-payment-${val}`}
                  className={`border rounded-lg p-4 text-center transition-colors ${form.payment_method === val ? "border-[#10056E] ring-1 ring-[#10056E] bg-indigo-50/50" : "border-slate-200"}`}>
                  <Icon className="h-6 w-6 mx-auto text-[#10056E]" />
                  <p className="text-sm font-bold mt-1.5">{label}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Resit (mod manual sahaja) */}
        {!form.receipt_image && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
          <SectionTitle icon={ReceiptText}>Resit (Wajib)</SectionTitle>
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-[#10056E]/60 transition-colors" data-testid="refuel-receipt-drop">
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="refuel-receipt-input" />
            <div className="py-4">
              <Upload className="h-6 w-6 mx-auto text-slate-400" />
              <p className="text-sm font-semibold mt-2">Ambil gambar / muat naik resit</p>
              <p className="text-xs text-muted-foreground">Imej sahaja, maksimum 5MB — AI akan menganalisis secara automatik</p>
            </div>
          </label>
        </div>
        )}

        <Button type="submit" disabled={busy || analyzing} className="w-full h-12 text-base bg-[#10056E] hover:bg-[#2a17a8]" data-testid="refuel-submit-btn">
          {busy ? "Menyimpan..." : "Rekod Isi Minyak"}
        </Button>
      </form>
      </div>
      )}
    </div>
  );
}
